// don't convert to TS yet as it's used in startup code that doesn't understand TS yet.
import uaParse from 'ua-parser-js';

import { EventTrackingConstants } from '../constants/EventTrackingConstants';

type StringOrStrings = string | string[];

function is(obj: any, nameOrNames: StringOrStrings) {
  if (typeof obj === 'undefined') {
    throw new Error('undefined UA obj argument');
  }

  const names = Array.isArray(nameOrNames) ? nameOrNames : [nameOrNames];

  if (names.includes(obj.name)) {
    return parseFloat(obj.version) || 0.9; // a truthy value that some older code may expect, but won't pass a version test
  } else {
    return NaN; // fea1123 had this, apparently returning the version num
  }
}

export default class UserAgent {
  result: any;

  constructor(userAgentSpec: string) {
    // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
    this.result = uaParse(userAgentSpec);
  }

  // Engines
  engine(names: StringOrStrings) {
    return is(this.result.engine, names);
  }

  gecko() {
    return this.engine('Gecko');
  }

  trident() {
    return this.engine('Trident');
  }

  webkit() {
    return this.engine('WebKit');
  }

  // Browsers
  browser(names: StringOrStrings) {
    return is(this.result.browser, names);
  }

  chrome() {
    return this.browser(['Chrome', 'Chromium']);
  }

  firefox() {
    return this.browser(['Firefox', 'Mozilla', 'Camino']);
  }

  ie(): number {
    return this.browser(['IE', 'IEMobile', 'IE Mobile', 'Edge']);
  }

  safari() {
    return this.browser(['Safari', 'Mobile Safari']);
  }

  opera() {
    return this.browser(['Opera', 'Opera Mobi', 'Opera Mini', 'Opera Tablet']);
  }

  // OS
  os(names: StringOrStrings) {
    return is(this.result.os, names);
  }

  osDescription() {
    return `${this.result.os.name || ''} ${this.result.os.version || ''}`.trim();
  }

  android() {
    return this.os('Android');
  }

  /**
   * @deprecated
   * @returns {Boolean} whether the user probably has an iOS device
   */
  ios() {
    return this.os('iOS') || (this.mac() && (this.phone() || this.tablet()));
  }

  mac() {
    return this.os('Mac OS');
  }

  windows() {
    return this.os(['Windows', 'Windows Phone', 'Window Mobile']);
  }

  // Device
  isDesktop() {
    // according https://github.com/faisalman/ua-parser-js/, possible types are only:
    // console, mobile, tablet, smarttv, wearable, embedded
    // ^ all not desktop
    return !this.result.device.type && !(this.android() || this.ios());
  }

  phone() {
    return this.result.device.type === 'mobile';
  }

  tablet() {
    return this.result.device.type === 'tablet';
  }

  isAndroidOrAppleTablet() {
    return this.tablet() && (this.android() || this.ios());
  }

  isAndroidOrApplePhone() {
    return this.phone() && (this.android() || this.ios());
  }

  mobile() {
    return this.tablet() || this.phone();
  }

  clientSegment() {
    let clientSegment: string = EventTrackingConstants.OTHER_WEB;

    if (this.mobile()) {
      if (this.android()) {
        clientSegment = EventTrackingConstants.ANDROID_MOBILE_WEB;
      } else if (this.ios()) {
        clientSegment = EventTrackingConstants.IOS_MOBILE_WEB;
      } else {
        clientSegment = EventTrackingConstants.OTHER_MOBILE_WEB;
      }
    } else if (this.isDesktop()) {
      clientSegment = EventTrackingConstants.DESKTOP_WEB;
    }

    return clientSegment;
  }

  noreferrerSupported() {
    return (
      this.chrome() >= 49 || this.firefox() >= 52 || this.safari() >= 10.3 || this.opera() >= 36
    );
  }
}
